import React, { useEffect, useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  Clock,
  Share2,
  ArrowRight,
  BookOpen,
  Sparkles,
  Layers,
} from 'lucide-react';
import { DEFAULT_HERO_IMAGE, fetchPublishedArticles } from '../data/blogData';

const BlogsPage = () => {
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Articles');
  const [searchQuery, setSearchQuery] = useState('');
  const [emailSubscribed, setEmailSubscribed] = useState(false);
  const [subscriberEmail, setSubscriberEmail] = useState('');
  const [shareToast, setShareToast] = useState(false);

  // Fetch live articles strictly from Payload CMS
  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);

    fetchPublishedArticles(controller.signal)
      .then((data) => {
        setArticles(data);
        setFetchError('');
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Error fetching CMS articles:', err);
          setFetchError('Unable to load articles right now. Please try again later.');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => controller.abort();
  }, []);

  // Compute categories dynamically from real CMS articles
  const categories = useMemo(() => {
    const set = new Set();
    articles.forEach((a) => {
      if (a.category) set.add(a.category);
    });
    return ['All Articles', ...Array.from(set)];
  }, [articles]);

  // Filter articles based on active category and search input
  const filteredArticles = useMemo(() => {
    return articles.filter((article) => {
      const matchesCategory =
        selectedCategory === 'All Articles' ||
        article.category?.toLowerCase() === selectedCategory.toLowerCase();
      const query = searchQuery.trim().toLowerCase();
      const matchesSearch =
        !query ||
        article.title.toLowerCase().includes(query) ||
        article.excerpt.toLowerCase().includes(query) ||
        article.tags?.some((tag) => tag.toLowerCase().includes(query));
      return matchesCategory && matchesSearch;
    });
  }, [articles, selectedCategory, searchQuery]);

  // Featured spotlight article (first article)
  const featuredArticle = useMemo(() => {
    return articles.find((a) => a.featured) || articles[0];
  }, [articles]);

  // Grid articles: when not searching and viewing "All Articles", exclude the spotlight card
  const gridArticles = useMemo(() => {
    if (searchQuery.trim() !== '' || selectedCategory !== 'All Articles') {
      return filteredArticles;
    }
    return filteredArticles.filter((a) => a.id !== featuredArticle?.id);
  }, [filteredArticles, featuredArticle, searchQuery, selectedCategory]);

  const handleShareArticle = (e, article) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/blog/${article.slug}`;
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      setShareToast(true);
    }
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (subscriberEmail) {
      setEmailSubscribed(true);
      setSubscriberEmail('');
    }
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      {/* ========================================================================= */}
      {/* 1. EDITORIAL MASTHEAD HERO (Sell.do Architecture + Original Coalitionify Content) */}
      {/* ========================================================================= */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 8, sm: 10, md: 12 },
          pb: { xs: 6, sm: 8, md: 9 },
          backgroundColor: '#FFFFFF',
          backgroundImage:
            'radial-gradient(ellipse 70% 50% at 50% -10%, rgba(37, 99, 235, 0.08) 0%, rgba(37, 99, 235, 0.04) 40%, rgba(255, 255, 255, 0) 80%)',
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
        }}
      >
        {/* Subtle Sell.do-style ambient floating blur spheres */}
        <Box
          sx={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 260,
            height: 260,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(37, 99, 235, 0.12) 0%, rgba(37, 99, 235, 0) 70%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: -80,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, rgba(37, 99, 235, 0) 70%)',
            pointerEvents: 'none',
            transform: 'translateY(-50%)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ maxWidth: 840, mx: 'auto', textAlign: 'center' }}>
            {/* Preserved Editorial Kicker */}
            <Chip
              label="COALITIONIFY EDITORIAL & PERSPECTIVES"
              sx={{
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                color: '#1E40AF',
                fontWeight: 700,
                fontSize: '0.76rem',
                letterSpacing: '0.08em',
                border: '1px solid rgba(37, 99, 235, 0.18)',
                mb: 2.5,
                py: 0.5,
              }}
            />

            {/* Preserved Classical Editorial Headline */}
            <Typography
              variant="h1"
              sx={{
                fontFamily: '"Plus Jakarta Sans", "Playfair Display", Georgia, serif',
                fontWeight: 800,
                fontSize: { xs: '2rem', sm: '3.2rem', md: '4rem' },
                lineHeight: 1.15,
                letterSpacing: '-0.035em',
                color: '#0F172A',
                mb: 2.5,
              }}
            >
              Insights on <span className="text-gradient-emerald">AI, CRM & Automation</span>
            </Typography>

            {/* Preserved Subtitle */}
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1.05rem', sm: '1.18rem' },
                color: '#475569',
                lineHeight: 1.7,
                maxWidth: 680,
                mx: 'auto',
                fontWeight: 500,
              }}
            >
              In-depth research, architectural breakdowns, and practical blueprints for high-velocity real estate operations and intelligent business systems.
            </Typography>

            
            <Box sx={{ mt: 4.5, maxWidth: 560, mx: 'auto' }}>
              <TextField
                fullWidth
                placeholder="Search articles, keywords, or topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                size="medium"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} color="#64748B" />
                    </InputAdornment>
                  ),
                  style: {
                    borderRadius: '9999px',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)',
                    border: '1px solid rgba(15, 23, 42, 0.1)',
                    paddingLeft: '10px',
                    fontSize: '0.95rem',
                  },
                }}
              />
            </Box>

            {/* Sell.do Category Filter Pills */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.2,
                mt: 3.5,
              }}
            >
              {categories.map((cat) => {
                const isActive = selectedCategory === cat;
                return (
                  <Chip
                    key={cat}
                    label={cat}
                    onClick={() => setSelectedCategory(cat)}
                    sx={{
                      px: 1.8,
                      py: 1,
                      fontSize: '0.84rem',
                      fontWeight: isActive ? 700 : 500,
                      backgroundColor: isActive ? 'rgba(37, 99, 235, 0.12)' : '#FFFFFF',
                      color: isActive ? '#1D4ED8' : '#64748B',
                      border: isActive ? '1.5px solid #2563EB' : '1px solid rgba(15, 23, 42, 0.12)',
                      cursor: 'pointer',
                      borderRadius: '9999px',
                      transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                      '&:hover': {
                        backgroundColor: isActive ? 'rgba(37, 99, 235, 0.18)' : '#F8FAFC',
                        borderColor: isActive ? '#2563EB' : 'rgba(15, 23, 42, 0.25)',
                        color: isActive ? '#1D4ED8' : '#0F172A',
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 2. LOADING OR ERROR STATE */}
      {/* ========================================================================= */}
      {isLoading && (
        <Box sx={{ py: 12, textAlign: 'center' }}>
          <CircularProgress size={44} sx={{ color: '#2563EB', mb: 2 }} />
          <Typography sx={{ color: '#64748B', fontSize: '0.95rem' }}>
            Loading publications from CMS...
          </Typography>
        </Box>
      )}

      {fetchError && !isLoading && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Container maxWidth="sm">
            <Alert severity="warning" sx={{ borderRadius: '12px' }}>
              {fetchError}
            </Alert>
          </Container>
        </Box>
      )}

      {/* ========================================================================= */}
      {/* 3. FEATURED ARTICLE (Sell.do 2-Column Banner Card) */}
      {/* ========================================================================= */}
      {!isLoading && featuredArticle && selectedCategory === 'All Articles' && !searchQuery.trim() && (
        <Box sx={{ pt: { xs: 6, md: 8 }, pb: 2 }}>
          <Container maxWidth="lg">
            <Typography
              sx={{
                mb: 2.5,
                fontSize: '0.8rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: '#64748B',
              }}
            >
              Featured Article
            </Typography>

            <Box
              className="card-glow"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
                backgroundColor: '#FFFFFF',
                borderRadius: '20px',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                overflow: 'hidden',
                boxShadow: '0 10px 30px -10px rgba(15, 23, 42, 0.06)',
              }}
            >
              {/* Left / Top: 16:10 Aspect Ratio Image */}
              <Box
                component={RouterLink}
                to={`/blog/${featuredArticle.slug}`}
                state={{ article: featuredArticle }}
                sx={{
                  position: 'relative',
                  display: 'block',
                  aspectRatio: { xs: '16/10', md: 'auto' },
                  minHeight: { md: 380 },
                  overflow: 'hidden',
                  backgroundColor: '#F8FAFC',
                  textDecoration: 'none',
                }}
              >
                <Box
                  component="img"
                  src={featuredArticle.heroImage}
                  alt={featuredArticle.title}
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = DEFAULT_HERO_IMAGE;
                  }}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                  loading="lazy"
                />
              </Box>

              {/* Right / Bottom: Content */}
              <Box
                sx={{
                  p: { xs: 3.5, sm: 4.5, md: 5 },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  {/* Category Pill + Updated Date */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Chip
                      label={featuredArticle.category}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(37, 99, 235, 0.12)',
                        color: '#1D4ED8',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        borderRadius: '9999px',
                      }}
                    />
                    <Typography sx={{ color: '#64748B', fontSize: '0.8rem', fontWeight: 500 }}>
                      {featuredArticle.publishedDate}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748B', fontSize: '0.8rem' }}>
                      <Clock size={13} />
                      <span>{featuredArticle.readTime}</span>
                    </Box>
                  </Box>

                  {/* Title linked to /blog/:slug */}
                  <Typography
                    component={RouterLink}
                    to={`/blog/${featuredArticle.slug}`}
                    state={{ article: featuredArticle }}
                    variant="h2"
                    sx={{
                      display: 'block',
                      fontFamily: '"Plus Jakarta Sans", sans-serif',
                      fontWeight: 800,
                      fontSize: { xs: '1.4rem', sm: '1.85rem', md: '2.1rem' },
                      lineHeight: 1.25,
                      color: '#0F172A',
                      textDecoration: 'none',
                      mb: 2,
                      letterSpacing: '-0.02em',
                      transition: 'color 0.2s ease',
                      '&:hover': {
                        color: '#2563EB',
                      },
                    }}
                  >
                    {featuredArticle.title}
                  </Typography>

                  {/* Excerpt */}
                  <Typography
                    sx={{
                      color: '#64748B',
                      fontSize: '0.98rem',
                      lineHeight: 1.65,
                      mb: 3,
                    }}
                  >
                    {featuredArticle.excerpt}
                  </Typography>
                </Box>

                {/* Footer with Author & Share */}
                <Box
                  sx={{
                    pt: 2.5,
                    borderTop: '1px solid rgba(15, 23, 42, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        background: 'linear-gradient(135deg, #0F172A 0%, #2563EB 100%)',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                      }}
                    >
                      {featuredArticle.author.initials}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0F172A' }}>
                        {featuredArticle.author.name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.74rem', color: '#64748B' }}>
                        {featuredArticle.author.role}
                      </Typography>
                    </Box>
                  </Box>

                  <IconButton
                    aria-label="Share this article"
                    onClick={(e) => handleShareArticle(e, featuredArticle)}
                    sx={{
                      width: 36,
                      height: 36,
                      border: '1px solid rgba(15, 23, 42, 0.12)',
                      color: '#64748B',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#2563EB',
                        color: '#2563EB',
                        backgroundColor: 'rgba(37, 99, 235, 0.06)',
                      },
                    }}
                  >
                    <Share2 size={16} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* ========================================================================= */}
      {/* 4. SELL.DO 3-COLUMN ARTICLES GRID */}
      {/* ========================================================================= */}
      {!isLoading && (
        <Box sx={{ py: { xs: 6, md: 8 } }}>
          <Container maxWidth="lg">
            {gridArticles.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <BookOpen size={48} color="#94A3B8" style={{ marginBottom: 16 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', mb: 1 }}>
                  No articles found
                </Typography>
                <Typography sx={{ color: '#64748B', fontSize: '0.95rem' }}>
                  {searchQuery
                    ? `We couldn't find any articles matching "${searchQuery}".`
                    : 'No articles published in this category yet.'}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={{ xs: 3, md: 4 }}>
                {gridArticles.map((article) => (
                  <Grid item xs={12} sm={6} lg={4} key={article.id}>
                    <Box
                      className="card-glow"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '18px',
                        border: '1px solid rgba(15, 23, 42, 0.08)',
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(15, 23, 42, 0.04)',
                        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 20px 35px -10px rgba(15, 23, 42, 0.08)',
                          borderColor: 'rgba(37, 99, 235, 0.3)',
                        },
                      }}
                    >
                      {/* Card Image with 16:10 Aspect Ratio & Floating Category */}
                      <Box
                        component={RouterLink}
                        to={`/blog/${article.slug}`}
                        state={{ article }}
                        sx={{
                          position: 'relative',
                          display: 'block',
                          aspectRatio: '16/10',
                          overflow: 'hidden',
                          backgroundColor: '#F8FAFC',
                          textDecoration: 'none',
                        }}
                      >
                        <Box
                          component="img"
                          src={article.heroImage}
                          alt={article.title}
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = DEFAULT_HERO_IMAGE;
                          }}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                            '&:hover': {
                              transform: 'scale(1.06)',
                            },
                          }}
                          loading="lazy"
                        />
                        {/* Sell.do floating category badge */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            px: 1.4,
                            py: 0.5,
                            borderRadius: '9999px',
                            backgroundColor: 'rgba(7, 16, 31, 0.72)',
                            backdropFilter: 'blur(8px)',
                            color: '#FFFFFF',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                          }}
                        >
                          {article.category}
                        </Box>
                      </Box>

                      {/* Card Body */}
                      <Box
                        sx={{
                          p: 3,
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box>
                          {/* Date & Read Time */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <Typography sx={{ color: '#64748B', fontSize: '0.78rem', fontWeight: 500 }}>
                              {article.publishedDate}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: '#64748B',
                                fontSize: '0.78rem',
                              }}
                            >
                              <Clock size={12} />
                              <span>{article.readTime}</span>
                            </Box>
                          </Box>

                          {/* Article Title */}
                          <Typography
                            component={RouterLink}
                            to={`/blog/${article.slug}`}
                            state={{ article }}
                            variant="h3"
                            sx={{
                              display: 'block',
                              fontFamily: '"Plus Jakarta Sans", sans-serif',
                              fontWeight: 700,
                              fontSize: '1.18rem',
                              lineHeight: 1.35,
                              color: '#0F172A',
                              textDecoration: 'none',
                              mb: 1.5,
                              letterSpacing: '-0.015em',
                              transition: 'color 0.2s ease',
                              '&:hover': {
                                color: '#2563EB',
                              },
                            }}
                          >
                            {article.title}
                          </Typography>

                          {/* Excerpt */}
                          <Typography
                            sx={{
                              color: '#64748B',
                              fontSize: '0.88rem',
                              lineHeight: 1.6,
                              mb: 2.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {article.excerpt}
                          </Typography>
                        </Box>

                        {/* Card Footer (Author & Share) */}
                        <Box
                          sx={{
                            pt: 2,
                            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                background: 'linear-gradient(135deg, #0F172A 0%, #2563EB 100%)',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                              }}
                            >
                              {article.author.initials}
                            </Avatar>
                            <Typography
                              sx={{
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                color: '#0F172A',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 140,
                              }}
                            >
                              {article.author.name}
                            </Typography>
                          </Box>

                          <IconButton
                            aria-label="Share article"
                            onClick={(e) => handleShareArticle(e, article)}
                            size="small"
                            sx={{
                              width: 32,
                              height: 32,
                              border: '1px solid rgba(15, 23, 42, 0.1)',
                              color: '#64748B',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: '#2563EB',
                                color: '#2563EB',
                                backgroundColor: 'rgba(37, 99, 235, 0.06)',
                              },
                            }}
                          >
                            <Share2 size={14} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Container>
        </Box>
      )}

      {/* ========================================================================= */}
      {/* 5. NEWSLETTER / SUBSCRIPTION CTA SECTION */}
      {/* ========================================================================= */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          backgroundColor: '#0F172A',
          color: '#FFFFFF',
          position: 'relative',
          overflow: 'hidden',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            height: 400,
            background: 'radial-gradient(circle, rgba(37, 99, 235, 0.12) 0%, rgba(37, 99, 235, 0.05) 50%, rgba(0, 0, 0, 0) 70%)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Box sx={{ maxWidth: 620, mx: 'auto' }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.8rem', sm: '1.9rem' },
                letterSpacing: '-0.025em',
                color: '#FFFFFF',
                mb: 1.5,
              }}
            >
              Stay Ahead of Real Estate &amp; AI Architecture
            </Typography>
            <Typography
              sx={{
                color: '#94A3B8',
                fontSize: { xs: '0.95rem', sm: '1.05rem' },
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              Get weekly insights on real estate tech, CRM automation, and high-velocity growth delivered straight to your inbox.
            </Typography>

            {emailSubscribed ? (
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: '14px',
                  backgroundColor: 'rgba(37, 99, 235, 0.15)',
                  border: '1px solid rgba(37, 99, 235, 0.3)',
                  color: '#3B82F6',
                  fontWeight: 600,
                  maxWidth: 480,
                  mx: 'auto',
                }}
              >
                🎉 Thank you for subscribing! You'll receive our next editorial dispatch.
              </Box>
            ) : (
              <Box
                component="form"
                onSubmit={handleSubscribe}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  maxWidth: 480,
                  mx: 'auto',
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <TextField
                  required
                  type="email"
                  fullWidth
                  placeholder="Enter your corporate email"
                  value={subscriberEmail}
                  onChange={(e) => setSubscriberEmail(e.target.value)}
                  variant="outlined"
                  size="medium"
                  InputProps={{
                    style: {
                      borderRadius: '9999px',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      color: '#FFFFFF',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      paddingLeft: '8px',
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    px: 3.5,
                    py: 1.5,
                    borderRadius: '9999px',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    backgroundColor: '#2563EB',
                    color: '#07101F',
                    boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
                    '&:hover': {
                      backgroundColor: '#3B82F6',
                    },
                  }}
                >
                  Subscribe
                </Button>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* Share Toast Notification */}
      <Snackbar
        open={shareToast}
        autoHideDuration={2500}
        onClose={() => setShareToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ borderRadius: '12px' }}>
          Article link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlogsPage;
